/** Inspections created against inspections closed, over the campaign's
 *  whole window, with the trajectory the deadline actually requires.
 *
 *  Three lines, and the third is the point: without the required
 *  trajectory the other two are just shapes. With it you can see at a
 *  glance that closures are flat against a line climbing to the goal.
 *
 *  Built for the campaign where the wave created ~100 inspections a day
 *  while ~5 were closed — a divergence no single figure on the page
 *  revealed.
 *
 *  Plotted against the real calendar (start → end), not against "days
 *  that had activity", so the empty stretch to the right is the time
 *  left rather than a gap in the data.
 */
import { useTranslation } from "react-i18next";

export type DayPoint = { date: string; created?: number; closed?: number };

const W = 320;
const H = 132;
const X0 = 16;
const X1 = 315;
const Y_TOP = 8;
const Y_BASE = 112;

const DAY = 86_400_000;

function utcDay(iso: string): number {
  return Math.floor(new Date(`${iso.slice(0, 10)}T00:00:00Z`).getTime() / DAY);
}

export function CreatedVsClosedChart({
  createdByDay,
  closedByDay,
  goalTotal,
  startDate,
  endDate,
  today,
}: {
  createdByDay: DayPoint[];
  closedByDay: DayPoint[];
  goalTotal: number;
  startDate?: string | null;
  endDate?: string | null;
  today?: Date;
}) {
  const { t } = useTranslation();

  const dates = Array.from(
    new Set([
      ...createdByDay.map((d) => d.date),
      ...closedByDay.map((d) => d.date),
    ]),
  ).sort();
  // One point is a dot, and a dot reads as a broken chart.
  if (dates.length < 2) return null;

  const createdMap = new Map(createdByDay.map((d) => [d.date, d.created ?? 0]));
  const closedMap = new Map(closedByDay.map((d) => [d.date, d.closed ?? 0]));

  // The x-axis is the campaign window when there is one, so "how far
  // through are we" is legible. Without an end date it falls back to the
  // span of the data itself.
  const firstDay = utcDay(startDate ?? dates[0]!);
  const lastDay = utcDay(endDate ?? dates[dates.length - 1]!);
  const span = Math.max(1, lastDay - firstDay);
  const x = (iso: string) =>
    X0 + ((utcDay(iso) - firstDay) / span) * (X1 - X0);

  const max = Math.max(goalTotal, 1);
  const y = (v: number) => Y_BASE - (v / max) * (Y_BASE - Y_TOP);

  let runC = 0;
  let runX = 0;
  const createdPts = dates.map((d) => {
    runC += createdMap.get(d) ?? 0;
    return { x: x(d), y: y(runC), v: runC };
  });
  const closedPts = dates.map((d) => {
    runX += closedMap.get(d) ?? 0;
    return { x: x(d), y: y(runX), v: runX };
  });

  // Carry both totals forward to today.
  //
  // `dates` holds only the days something HAPPENED, so without this the
  // lines stop at the last day of activity while the "today" marker
  // stands at today — and the endpoint dots sit to the left of it by
  // however long the campaign has been quiet. Reported 2026-10-01: the
  // dashed vertical line did not pass through the dots. A weekend, or
  // simply the hours before the morning wave runs, is enough to open
  // the gap; it closes again by itself once a session is created, which
  // is why it looks intermittent.
  //
  // Flat, because these are cumulative: nothing happening does not
  // reduce the number created, it holds it. The line going sideways to
  // today is the true shape of a quiet stretch, and reading that
  // flatness against the climbing required line is the whole point of
  // the chart.
  const todayKey = today ? today.toISOString().slice(0, 10) : null;
  const lastDataDay = utcDay(dates[dates.length - 1]!);
  if (todayKey && utcDay(todayKey) > lastDataDay) {
    // Clamped at X1: past the deadline the marker pins to the end of
    // the window rather than running off the plot.
    const tx = Math.min(X1, x(todayKey));
    createdPts.push({ x: tx, y: y(runC), v: runC });
    closedPts.push({ x: tx, y: y(runX), v: runX });
  }

  const line = (pts: { x: number; y: number }[]) =>
    pts.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  const lastCreated = createdPts[createdPts.length - 1]!;
  const lastClosed = closedPts[closedPts.length - 1]!;
  // Derived from the series, so the marker and the dots cannot disagree
  // — they are the same point by construction now, not by coincidence.
  const nowX = lastCreated.x;

  return (
    <section className="card">
      <header className="mb-2 flex items-baseline gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("campaigns.chart.title")}
        </h2>
        <span className="ml-auto text-xs text-nb-text-3">
          {t("campaigns.chart.range", { count: dates.length })}
        </span>
      </header>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block h-auto w-full"
        role="img"
        aria-label={t("campaigns.chart.alt", {
          created: lastCreated.v,
          closed: lastClosed.v,
        })}
      >
        {[0, 0.5, 1].map((f) => (
          <line
            key={f}
            x1={X0}
            x2={X1}
            y1={y(max * f)}
            y2={y(max * f)}
            stroke="currentColor"
            strokeWidth="0.5"
            className="text-nb-border"
          />
        ))}
        {[
          { v: max, at: y(max) + 3 },
          { v: Math.round(max / 2), at: y(max / 2) + 3 },
          { v: 0, at: Y_BASE + 3 },
        ].map((g) => (
          <text
            key={g.v}
            x="0"
            y={g.at}
            fontSize="6"
            fontFamily="IBM Plex Mono, monospace"
            fill="currentColor"
            className="text-nb-text-3"
          >
            {g.v}
          </text>
        ))}

        {/* The trajectory the deadline needs: straight from the start of
            the window to the whole book by the end of it. */}
        {endDate && (
          <>
            <line
              x1={X0}
              y1={Y_BASE}
              x2={X1}
              y2={y(max)}
              stroke="#DC2626"
              strokeWidth="1.3"
              strokeDasharray="4 3"
            />
            <text
              x={X1 - 4}
              y={y(max) + 12}
              fontSize="6.5"
              textAnchor="end"
              fontFamily="IBM Plex Mono, monospace"
              fill="#FCA5A5"
            >
              {t("campaigns.chart.required")}
            </text>
          </>
        )}

        <path d={line(createdPts)} fill="none" stroke="#0EA5E9" strokeWidth="1.8" />
        <path d={line(closedPts)} fill="none" stroke="#00E5A0" strokeWidth="2" />
        <circle cx={lastCreated.x} cy={lastCreated.y} r="2.2" fill="#0EA5E9" />
        <circle cx={lastClosed.x} cy={lastClosed.y} r="2.2" fill="#00E5A0" />
        <text
          x={lastCreated.x + 5}
          y={lastCreated.y - 1}
          fontSize="6.5"
          fontFamily="IBM Plex Mono, monospace"
          fill="#0EA5E9"
        >
          {lastCreated.v.toLocaleString()}
        </text>
        <text
          x={lastClosed.x + 5}
          y={lastClosed.y + 5}
          fontSize="6.5"
          fontFamily="IBM Plex Mono, monospace"
          fill="#00E5A0"
        >
          {lastClosed.v.toLocaleString()}
        </text>

        {today && (
          <>
            <line
              x1={nowX}
              y1={Y_TOP}
              x2={nowX}
              y2={Y_BASE + 4}
              stroke="currentColor"
              strokeWidth="0.5"
              strokeDasharray="2 2"
              className="text-nb-text-3"
            />
            <text
              x={nowX}
              y={H - 3}
              fontSize="5.5"
              textAnchor="middle"
              fontFamily="IBM Plex Mono, monospace"
              fill="currentColor"
              className="text-nb-text-3"
            >
              {t("campaigns.chart.today")}
            </text>
          </>
        )}
      </svg>

      <dl className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-xs text-nb-text-2">
        <div className="flex items-center gap-1.5">
          <span aria-hidden className="h-0.5 w-3.5 bg-sky-500" />
          <dt>{t("campaigns.chart.created")}</dt>
          <dd className="font-mono text-nb-text">
            {lastCreated.v.toLocaleString()}
          </dd>
        </div>
        <div className="flex items-center gap-1.5">
          <span aria-hidden className="h-0.5 w-3.5 bg-nb-mint" />
          <dt>{t("campaigns.chart.closed")}</dt>
          <dd className="font-mono text-nb-text">
            {lastClosed.v.toLocaleString()}
          </dd>
        </div>
        {endDate && (
          <div className="flex items-center gap-1.5">
            <span
              aria-hidden
              className="h-0 w-3.5 border-t border-dashed border-cvp-danger"
            />
            <dt>{t("campaigns.chart.requiredLegend")}</dt>
          </div>
        )}
      </dl>
    </section>
  );
}
